<script setup>
import { onMounted, ref } from "vue"
import Swal from "sweetalert2"
import api from "@/api/axios.js"

const TABS = [
  { tipo: "cargo", label: "Cargos" },
  { tipo: "area", label: "Áreas" },
  { tipo: "tipoContrato", label: "Tipos de Contrato" },
  { tipo: "eps", label: "EPS" },
  { tipo: "fondoPension", label: "Fondos de Pensión" },
  { tipo: "arl", label: "ARL" },
  { tipo: "ciudadAtencion", label: "Ciudades de Atención" }
]

const tipoActivo = ref("cargo")
const items = ref([])
const cargando = ref(true)
const nuevoNombre = ref("")
const guardando = ref(false)

async function cargar() {
  cargando.value = true
  try {
    const { data } = await api.get(`/catalogos/${tipoActivo.value}`, { params: { incluirInactivos: true } })
    items.value = data.items
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo cargar el catálogo",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  } finally {
    cargando.value = false
  }
}

function cambiarTab(tipo) {
  tipoActivo.value = tipo
  nuevoNombre.value = ""
  cargar()
}

onMounted(cargar)

async function agregar() {
  if (!nuevoNombre.value.trim()) return

  guardando.value = true
  try {
    await api.post(`/catalogos/${tipoActivo.value}`, { nombre: nuevoNombre.value.trim() })
    nuevoNombre.value = ""
    await cargar()
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo agregar",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  } finally {
    guardando.value = false
  }
}

async function renombrar(item) {
  const { value: nombre, isConfirmed } = await Swal.fire({
    icon: "question",
    title: "Renombrar",
    input: "text",
    inputValue: item.nombre,
    showCancelButton: true,
    confirmButtonText: "Guardar",
    cancelButtonText: "Cancelar"
  })
  if (!isConfirmed || !nombre?.trim()) return

  try {
    await api.put(`/catalogos/${tipoActivo.value}/${item.id}`, { nombre: nombre.trim() })
    await cargar()
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo renombrar",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  }
}

async function alternarActivo(item) {
  try {
    if (item.activo) {
      await api.delete(`/catalogos/${tipoActivo.value}/${item.id}`)
    } else {
      await api.put(`/catalogos/${tipoActivo.value}/${item.id}`, { activo: true })
    }
    await cargar()
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo actualizar",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  }
}
</script>

<template>
  <div class="container-fluid py-4">
    <div class="card shadow-sm">
      <div class="card-header">
        <h5 class="mb-3"><i class="ni ni-settings-gear-65 text-primary me-2"></i>Catálogos</h5>
        <ul class="nav nav-pills">
          <li class="nav-item" v-for="tab in TABS" :key="tab.tipo">
            <a
              href="#"
              class="nav-link"
              :class="{ active: tipoActivo === tab.tipo }"
              @click.prevent="cambiarTab(tab.tipo)"
            >
              {{ tab.label }}
            </a>
          </li>
        </ul>
      </div>

      <div class="card-body">
        <form class="row g-2 mb-3" @submit.prevent="agregar">
          <div class="col-md-6">
            <input type="text" class="form-control" v-model="nuevoNombre" placeholder="Nuevo valor...">
          </div>
          <div class="col-md-3">
            <button type="submit" class="btn btn-primary mb-0" :disabled="guardando">
              <i class="ni ni-fat-add me-1"></i> Agregar
            </button>
          </div>
        </form>

        <div v-if="cargando" class="text-center text-muted py-5">
          <div class="spinner-border spinner-border-sm me-2" role="status"></div>
          Cargando...
        </div>

        <div v-else class="table-responsive">
          <table class="table table-striped table-bordered align-middle table-sm">
            <thead class="table-dark">
              <tr>
                <th>Nombre</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="items.length === 0">
                <td colspan="3" class="text-center text-muted py-4">Sin valores registrados todavía.</td>
              </tr>
              <tr v-for="item in items" :key="item.id">
                <td>{{ item.nombre }}</td>
                <td>
                  <span class="badge" :class="item.activo ? 'bg-success' : 'bg-secondary'">
                    {{ item.activo ? "Activo" : "Inactivo" }}
                  </span>
                </td>
                <td>
                  <div class="d-flex gap-1">
                    <button type="button" class="btn btn-sm btn-outline-primary" title="Renombrar" @click="renombrar(item)">
                      <i class="bi bi-pencil"></i>
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm"
                      :class="item.activo ? 'btn-outline-danger' : 'btn-outline-success'"
                      :title="item.activo ? 'Desactivar' : 'Activar'"
                      @click="alternarActivo(item)"
                    >
                      <i class="bi" :class="item.activo ? 'bi-eye-slash' : 'bi-eye'"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>
